import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";

const MENSAJE_POR_DEFECTO =
  "Esta página ha sido desactivada por la administración. Para mayor información, comuníquese con la Comandancia.";

const LEGACY_KEYS = {
  kf83mx: "cia_kf83mx_activa",
  zl29qp: "cia_zl29qp_activa",
  rd77lt: "cia_rd77lt_activa",
  vb52cz: "cia_vb52cz_activa",
  wn64dy: "cia_wn64dy_activa",
};

function baseConfig(cuerpoId) {
  return {
    cuerpo_id: cuerpoId,
    landing_activa: true,
    global_activa: true,
    mensaje_desactivacion: MENSAJE_POR_DEFECTO,
    companias: {},
  };
}

export default function ConfiguracionVisibilidad() {
  const { cuerpo, cuerpoId } = useCuerpo();
  const [companias, setCompanias] = useState([]);
  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState("");

  const refConfig = useMemo(
    () => (cuerpoId ? doc(db, "configuracion_visibilidad", cuerpoId) : null),
    [cuerpoId]
  );

  useEffect(() => {
    let activo = true;

    async function cargar() {
      if (!cuerpoId || !refConfig) return;
      setCargando(true);
      try {
        const [snapCias, snapCfg] = await Promise.all([
          getDocs(query(collection(db, "companias"), where("cuerpo_id", "==", cuerpoId))),
          getDoc(refConfig),
        ]);

        const cias = snapCias.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => Number(a.orden || 9999) - Number(b.orden || 9999));

        let next = baseConfig(cuerpoId);

        if (snapCfg.exists()) {
          next = { ...next, ...snapCfg.data(), companias: snapCfg.data()?.companias || {} };
        } else {
          // Migración suave de la antigua configuración única de Huasco.
          if (String(cuerpoId).toLowerCase() === "huasco") {
            try {
              const legacy = await getDoc(doc(db, "configuracion", "visibilidad"));
              if (legacy.exists()) {
                const ld = legacy.data() || {};
                next.landing_activa = ld.landing_activa !== false;
                next.global_activa = ld.global_activa !== false;
                next.mensaje_desactivacion = ld.mensaje_desactivacion || MENSAJE_POR_DEFECTO;
                cias.forEach((c) => {
                  const slug = String(c.slug || "").toLowerCase();
                  const legacyKey = LEGACY_KEYS[slug];
                  next.companias[slug] = legacyKey ? ld[legacyKey] !== false : true;
                });
              }
            } catch (e) {
              console.warn("No se pudo leer configuración legacy de Huasco:", e);
            }
          }

          cias.forEach((c) => {
            const slug = String(c.slug || c.id).toLowerCase();
            if (next.companias[slug] == null) next.companias[slug] = true;
          });

          await setDoc(refConfig, { ...next, createdAt: Timestamp.now(), updatedAt: Timestamp.now() }, { merge: true });
        }

        cias.forEach((c) => {
          const slug = String(c.slug || c.id).toLowerCase();
          if (next.companias[slug] == null) next.companias[slug] = true;
        });

        if (activo) {
          setCompanias(cias);
          setConfig(next);
        }
      } catch (error) {
        console.error("Error cargando visibilidad por Cuerpo:", error);
        if (activo) {
          setCompanias([]);
          setConfig(baseConfig(cuerpoId));
          setMensaje("No se pudo cargar la configuración. Revisa permisos de Firestore.");
        }
      } finally {
        if (activo) setCargando(false);
      }
    }

    cargar();
    return () => { activo = false; };
  }, [cuerpoId, refConfig]);

  async function guardarParcial(patch, nextState) {
    if (!refConfig) return;
    setGuardando(true);
    setMensaje("");
    try {
      await setDoc(refConfig, { cuerpo_id: cuerpoId, ...patch, updatedAt: Timestamp.now() }, { merge: true });
      setConfig(nextState);
      setMensaje("Configuración actualizada correctamente.");
    } catch (e) {
      console.error(e);
      setMensaje("Error al guardar la configuración.");
    } finally {
      setGuardando(false);
      setTimeout(() => setMensaje(""), 3000);
    }
  }

  const toggleCampo = async (campo) => {
    const next = { ...config, [campo]: !config[campo] };
    await guardarParcial({ [campo]: next[campo] }, next);
  };

  const toggleCompania = async (cia) => {
    const slug = String(cia.slug || cia.id).toLowerCase();
    const mapa = { ...(config.companias || {}), [slug]: config.companias?.[slug] === false };
    const next = { ...config, companias: mapa };
    await guardarParcial({ companias: mapa }, next);
  };

  const guardarMensaje = async () => {
    const texto = String(config.mensaje_desactivacion || "").trim() || MENSAJE_POR_DEFECTO;
    const next = { ...config, mensaje_desactivacion: texto };
    await guardarParcial({ mensaje_desactivacion: texto }, next);
  };

  if (cargando || !config) return <div>Cargando configuración de visibilidad...</div>;

  return (
    <div style={{ padding: 16 }}>
      <h2>Configuración de visibilidad</h2>
      <p style={{ opacity: .8 }}>
        Configuración exclusiva de <strong>{cuerpo?.nombre || cuerpoId}</strong>. Los cambios no afectan a otros Cuerpos.
      </p>

      <OpcionSwitch label="Página principal (Landing)" checked={config.landing_activa !== false} onChange={() => toggleCampo("landing_activa")} />
      <OpcionSwitch label="Vista Global" checked={config.global_activa !== false} onChange={() => toggleCampo("global_activa")} />

      <hr style={{ margin: "16px 0" }} />
      <h3>Compañías</h3>
      {companias.map((cia) => {
        const slug = String(cia.slug || cia.id).toLowerCase();
        return (
          <OpcionSwitch
            key={cia.id}
            label={`${cia.nombre || cia.id} (/cia/${slug})`}
            checked={config.companias?.[slug] !== false}
            onChange={() => toggleCompania(cia)}
          />
        );
      })}
      {!companias.length && <p>No hay compañías configuradas para este Cuerpo.</p>}

      <hr style={{ margin: "20px 0" }} />
      <h3>Mensaje para páginas desactivadas</h3>
      <textarea
        style={{ width: "100%", minHeight: 100, marginTop: 8 }}
        value={config.mensaje_desactivacion || ""}
        onChange={(e) => setConfig((prev) => ({ ...prev, mensaje_desactivacion: e.target.value }))}
        placeholder={MENSAJE_POR_DEFECTO}
      />
      <button style={{ marginTop: 8 }} onClick={guardarMensaje} disabled={guardando}>Guardar mensaje</button>

      {guardando && <p>Guardando cambios...</p>}
      {mensaje && <p>{mensaje}</p>}
    </div>
  );
}

function OpcionSwitch({ label, checked, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, padding: "8px 0", borderBottom: "1px solid #ddd" }}>
      <span>{label}</span>
      <input type="checkbox" checked={!!checked} onChange={onChange} />
    </label>
  );
}
